import React, { useState, useEffect, useContext } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import axios from 'axios';

import CurrentTournament from './pages/CurrentTournament';
import TournamentDetail from './pages/TournamentDetail';
import TournamentSchedule from './pages/TournamentSchedule';
import PastTournaments from './pages/PastTournaments';
import PresentationView from './pages/PresentationView';
import AdminLogin from './pages/AdminLogin';
import TournamentCreate from './pages/TournamentCreate';
import TournamentEdit from './pages/TournamentEdit';
import TournamentDelete from './pages/TournamentDelete';
import AdminTournamentBracket from './pages/AdminTournamentBracket';
import UserManagement from './pages/UserManagement';
import UserProfile from './pages/UserProfile';
import SystemSettings from './pages/SystemSettings';
import ActivityLog from './pages/ActivityLog';
import Impressum from './pages/Impressum';
import SuperAdmin from './pages/SuperAdmin';
import ChangePassword from './pages/ChangePassword';
import LandingPage from './pages/LandingPage';
import Datenschutz from './pages/Datenschutz';
import Kontakt from './pages/Kontakt';
import Footer from './components/Footer';
import ThemeToggle from './components/ThemeToggle';
import LanguageDropdown from './components/LanguageDropdown';
import { SettingsProvider, SettingsContext } from './contexts/SettingsContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { PlanContext } from './contexts/PlanContext';

// Configure axios base URL and credentials
axios.defaults.baseURL = process.env.REACT_APP_API_URL || '';
axios.defaults.withCredentials = true;

function AppContent() {
  const [userRole, setUserRole] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [isTenant, setIsTenant] = useState(null); // null = noch laden
  const [planInfo, setPlanInfo] = useState({ plan: 'pro', limits: null });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Settings aus Context laden
  const { settings } = useContext(SettingsContext);
  const logoUrl = settings.logoUrl || 'https://x3padel.de/wp-content/uploads/2025/09/horizontal-black-portreslogo.svg';
  const showSponsor = settings.showSponsor;
  const sponsors = settings.sponsors || [];
  const sponsorRotationMs = (settings.sponsorRotationDuration || 5) * 1000;
  const [sponsorIndex, setSponsorIndex] = useState(0);
  const [sponsorVisible, setSponsorVisible] = useState(true);

  // Rotiere Sponsoren nach konfigurierbarer Zeit, mit Fade-Effekt
  useEffect(() => {
    if (sponsors.length <= 1) return;
    const interval = setInterval(() => {
      setSponsorVisible(false);
      setTimeout(() => {
        setSponsorIndex(prev => (prev + 1) % sponsors.length);
        setSponsorVisible(true);
      }, 400);
    }, sponsorRotationMs);
    return () => clearInterval(interval);
  }, [sponsors.length, sponsorRotationMs]);

  const currentSponsor = sponsors.length > 0 ? sponsors[sponsorIndex % sponsors.length] : null;

  // Prüfe ob wir auf der Present-Seite sind
  const isFullScreen = location.pathname === '/present';

  // Plausible pageview bei Routenwechsel (SPA-Tracking)
  useEffect(() => {
    if (typeof window.plausible === 'function') {
      window.plausible('pageview');
    }
  }, [location.pathname]);

  // Tenant-Info und User-Info parallel beim Start laden
  useEffect(() => {
    axios.get('/api/tenant-info')
      .then(res => {
        setIsTenant(res.data.is_tenant);
        if (res.data.is_tenant && res.data.limits) {
          setPlanInfo({ plan: res.data.plan, limits: res.data.limits });
        }
      })
      .catch(() => setIsTenant(true)); // Fallback: als Tenant behandeln

    axios.get('/api/users/me')
      .then(res => {
        setUserRole(res.data.role);
        setMustChangePassword(res.data.must_change_password || false);
      })
      .catch(err => {
        if (err.response?.status === 401 || err.response?.status === 403) {
          setUserRole(null);
          setMustChangePassword(false);
        }
      });
  }, []);

  const handleLogout = async () => {
    await axios.post('/auth/logout').catch(() => {});
    setUserRole(null);
    navigate('/admin');
  };

  // Hauptdomain → Landing Page anzeigen (außer für den SuperAdmin-Zugang, der nur auf der Hauptdomain existiert)
  if (isTenant === false && location.pathname !== '/superadmin') {
    return <LandingPage />;
  }

  // Noch laden → nichts rendern (verhindert kurzes Aufblitzen)
  if (isTenant === null) {
    return null;
  }

  return (
    <PlanContext.Provider value={planInfo}>
    <div className="app-container">
      {/* Hauptmenü - nicht anzeigen auf Fullscreen-Seite */}
      {!isFullScreen && <nav className="navbar">
        {/* Overlay für mobiles Menü - innerhalb navbar für korrekten stacking context */}
        {mobileMenuOpen && (
          <div
            className="mobile-menu-overlay"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
        )}

        {/* Desktop Navigation - Komplett neue Struktur */}
        <div className="navbar-desktop desktop-only">
          {/* Obere Zeile: Logo + Public Navigation */}
          <div className="navbar-top">
            <div className="navbar-logo">
              <img
                src={logoUrl}
                alt="Portres Padel Club"
                className="logo-image"
              />
              {showSponsor && currentSponsor && (
                <span style={{ opacity: sponsorVisible ? 1 : 0, transition: 'opacity 0.4s ease', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <span className="powered-by-text">powered by</span>
                  {currentSponsor.link ? (
                    <a href={currentSponsor.link} target="_blank" rel="noopener noreferrer">
                      <img src={currentSponsor.logo_url} alt={currentSponsor.name || 'Sponsor'} className="logo-image logo-sponsor" />
                    </a>
                  ) : (
                    <img src={currentSponsor.logo_url} alt={currentSponsor.name || 'Sponsor'} className="logo-image logo-sponsor" />
                  )}
                </span>
              )}
            </div>

            <nav className="navbar-public-nav">
              <NavLink
                to="/"
                end
                className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
              >
                {t('nav.currentTournaments')}
              </NavLink>
              {(!planInfo.limits || planInfo.limits.presentation_view) && (
                <NavLink
                  to="/present"
                  className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
                >
                  {t('nav.presentationView')}
                </NavLink>
              )}
              <NavLink
                to="/past"
                className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'}
              >
                {t('nav.pastTournaments')}
              </NavLink>

              {!userRole && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) => isActive ? 'nav-item nav-item-admin active' : 'nav-item nav-item-admin'}
                >
                  {t('nav.adminLogin')}
                </NavLink>
              )}
              <LanguageDropdown />
            </nav>
          </div>

          {/* Untere Zeile: Admin Navigation - nur wenn eingeloggt */}
          {userRole && (
            <div className="navbar-admin">
              <div className="navbar-admin-label">{t('nav.adminArea')}</div>
              <nav className="navbar-admin-nav">
                <NavLink
                  to="/admin/profile"
                  className={({ isActive }) => isActive ? 'nav-admin-item active' : 'nav-admin-item'}
                >
                  {t('nav.myProfile')}
                </NavLink>

                {(userRole === 'tournament_admin' || userRole === 'full_admin') && (
                  <>
                    <NavLink
                      to="/admin/bracket"
                      className={({ isActive }) => isActive ? 'nav-admin-item active' : 'nav-admin-item'}
                    >
                      {t('nav.bracket')}
                    </NavLink>
                    <NavLink
                      to="/admin/edit"
                      className={({ isActive }) => isActive ? 'nav-admin-item active' : 'nav-admin-item'}
                    >
                      {t('nav.editTournament')}
                    </NavLink>
                    <NavLink
                      to="/admin/create"
                      className={({ isActive }) => isActive ? 'nav-admin-item active' : 'nav-admin-item'}
                    >
                      {t('nav.createTournament')}
                    </NavLink>
                    <NavLink
                      to="/admin/delete"
                      className={({ isActive }) => isActive ? 'nav-admin-item active' : 'nav-admin-item'}
                    >
                      {t('nav.deleteTournament')}
                    </NavLink>
                  </>
                )}

                {userRole === 'full_admin' && (
                  <>
                    {(!planInfo.limits || planInfo.limits.multiple_admins) && (
                      <NavLink
                        to="/admin/users"
                        className={({ isActive }) => isActive ? 'nav-admin-item active' : 'nav-admin-item'}
                      >
                        {t('nav.userManagement')}
                      </NavLink>
                    )}
                    <NavLink
                      to="/admin/settings"
                      className={({ isActive }) => isActive ? 'nav-admin-item active' : 'nav-admin-item'}
                    >
                      {t('nav.systemSettings')}
                    </NavLink>
                    {(!planInfo.limits || planInfo.limits.activity_log) && (
                      <NavLink
                        to="/admin/logs"
                        className={({ isActive }) => isActive ? 'nav-admin-item active' : 'nav-admin-item'}
                      >
                        {t('nav.activityLog')}
                      </NavLink>
                    )}
                  </>
                )}
              </nav>

              <button onClick={handleLogout} className="navbar-logout-btn">
                {t('nav.logout')}
              </button>
            </div>
          )}
        </div>

        {/* Mobile Navigation - unverändert */}
        <div className="navbar-mobile">
          <div className="navbar-mobile-top">
            <div className="navbar-logo">
              <img
                src={logoUrl}
                alt="Portres Padel Club"
                className="logo-image"
              />
              {showSponsor && currentSponsor && (
                <span style={{ opacity: sponsorVisible ? 1 : 0, transition: 'opacity 0.4s ease', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <span className="powered-by-text">powered by</span>
                  {currentSponsor.link ? (
                    <a href={currentSponsor.link} target="_blank" rel="noopener noreferrer">
                      <img src={currentSponsor.logo_url} alt={currentSponsor.name || 'Sponsor'} className="logo-image logo-sponsor" />
                    </a>
                  ) : (
                    <img src={currentSponsor.logo_url} alt={currentSponsor.name || 'Sponsor'} className="logo-image logo-sponsor" />
                  )}
                </span>
              )}
            </div>

            <button
              className="hamburger-menu"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Menü"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>

        {/* Mobile Navigation - im Hamburger Menü */}
        <div className={`navbar-links navbar-links-mobile ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Frontend Links */}
          <NavLink
            to="/"
            end
            className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('nav.currentTournaments')}
          </NavLink>
          {(!planInfo.limits || planInfo.limits.presentation_view) && (
            <NavLink
              to="/present"
              className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.presentationView')}
            </NavLink>
          )}
          <NavLink
            to="/past"
            className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('nav.pastTournaments')}
          </NavLink>

          {!userRole && (
            <NavLink
              to="/admin"
              className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.adminLogin')}
            </NavLink>
          )}

          {userRole && (
            <>
              <div className="mobile-menu-separator"></div>

              <NavLink
                to="/admin/profile"
                className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                onClick={() => setMobileMenuOpen(false)}
              >
                {t('nav.myProfile')}
              </NavLink>

              {(userRole === 'tournament_admin' || userRole === 'full_admin') && (
                <>
                  <NavLink
                    to="/admin/bracket"
                    className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.bracket')}
                  </NavLink>
                  <NavLink
                    to="/admin/edit"
                    className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.editTournament')}
                  </NavLink>
                  <NavLink
                    to="/admin/create"
                    className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.createTournament')}
                  </NavLink>
                  <NavLink
                    to="/admin/delete"
                    className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.deleteTournament')}
                  </NavLink>
                </>
              )}

              {userRole === 'full_admin' && (
                <>
                  {(!planInfo.limits || planInfo.limits.multiple_admins) && (
                    <NavLink
                      to="/admin/users"
                      className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.userManagement')}
                    </NavLink>
                  )}
                  <NavLink
                    to="/admin/settings"
                    className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.systemSettings')}
                  </NavLink>
                  {(!planInfo.limits || planInfo.limits.activity_log) && (
                    <NavLink
                      to="/admin/logs"
                      className={({ isActive }) => 'nav-link' + (isActive ? ' active' : '')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.activityLog')}
                    </NavLink>
                  )}
                </>
              )}

              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="logout-button"
              >
                🚪 {t('nav.logout')}
              </button>
            </>
          )}
          <div style={{ padding: '8px 16px', borderTop: '1px solid rgba(148,163,184,0.15)', marginTop: '4px' }}>
            <LanguageDropdown compact />
          </div>
        </div>
      </nav>}

      {/* Sponsor Banner - nicht anzeigen auf Fullscreen-Seite */}
      {!isFullScreen && showSponsor && currentSponsor && <div className="sponsor-banner" style={{ opacity: sponsorVisible ? 1 : 0, transition: 'opacity 0.4s ease' }}>
        <span className="sponsor-text">powered by</span>
        {currentSponsor.link ? (
          <a href={currentSponsor.link} target="_blank" rel="noopener noreferrer">
            <img src={currentSponsor.logo_url} alt={currentSponsor.name || 'Sponsor'} className="sponsor-logo" />
          </a>
        ) : (
          <img src={currentSponsor.logo_url} alt={currentSponsor.name || 'Sponsor'} className="sponsor-logo" />
        )}
      </div>}

      <main className="page-content">
        <Routes>
          {/* Öffentliche Seiten */}
          <Route path="/" element={<CurrentTournament />} />
          <Route path="/tournament/:id" element={<TournamentDetail />} />
          <Route path="/tournament/:id/schedule" element={<TournamentSchedule />} />
          <Route path="/past" element={<PastTournaments />} />
          <Route path="/present" element={<PresentationView />} />
          <Route path="/impressum" element={<Impressum />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/kontakt" element={<Kontakt />} />

          {/* Admin-Login */}
          <Route
            path="/admin"
            element={
              userRole ? <Navigate to="/admin/profile" replace /> : <AdminLogin />
            }
          />

          {/* Passwortänderung (erzwungen nach erstem Login) */}
          <Route
            path="/admin/change-password"
            element={
              !userRole ? <Navigate to="/admin" replace /> :
              <ChangePassword onPasswordChanged={() => setMustChangePassword(false)} />
            }
          />

          {/* Mein Profil - für alle eingeloggten User */}
          <Route
            path="/admin/profile"
            element={
              !userRole ? <Navigate to="/admin" replace /> :
              mustChangePassword ? <Navigate to="/admin/change-password" replace /> :
              <UserProfile />
            }
          />

          {/* Benutzerverwaltung - nur für Full Admin */}
          <Route
            path="/admin/users"
            element={
              !userRole ? <Navigate to="/admin" replace /> :
              mustChangePassword ? <Navigate to="/admin/change-password" replace /> :
              <UserManagement />
            }
          />

          {/* System-Einstellungen - nur für Full Admin */}
          <Route
            path="/admin/settings"
            element={
              !userRole ? <Navigate to="/admin" replace /> :
              mustChangePassword ? <Navigate to="/admin/change-password" replace /> :
              <SystemSettings />
            }
          />

          {/* Aktivitäts-Log - nur für Full Admin */}
          <Route
            path="/admin/logs"
            element={
              userRole !== 'full_admin' ? <Navigate to="/admin" replace /> :
              mustChangePassword ? <Navigate to="/admin/change-password" replace /> :
              <ActivityLog />
            }
          />

          {/* Turnier-Verwaltung - für Tournament Admin und Full Admin */}
          <Route
            path="/admin/bracket"
            element={
              !userRole ? <Navigate to="/admin" replace /> :
              mustChangePassword ? <Navigate to="/admin/change-password" replace /> :
              <AdminTournamentBracket />
            }
          />
          <Route
            path="/admin/create"
            element={
              !userRole ? <Navigate to="/admin" replace /> :
              mustChangePassword ? <Navigate to="/admin/change-password" replace /> :
              <TournamentCreate />
            }
          />
          <Route
            path="/admin/edit/:id"
            element={
              !userRole ? <Navigate to="/admin" replace /> :
              mustChangePassword ? <Navigate to="/admin/change-password" replace /> :
              <TournamentEdit />
            }
          />
          <Route
            path="/admin/edit"
            element={
              !userRole ? <Navigate to="/admin" replace /> :
              mustChangePassword ? <Navigate to="/admin/change-password" replace /> :
              <TournamentEdit />
            }
          />
          <Route
            path="/admin/delete"
            element={
              !userRole ? <Navigate to="/admin" replace /> :
              mustChangePassword ? <Navigate to="/admin/change-password" replace /> :
              <TournamentDelete />
            }
          />

          {/* Fallback */}
          <Route path="/superadmin" element={<SuperAdmin />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {/* Footer - nicht anzeigen auf Fullscreen-Seite */}
      {!isFullScreen && <Footer />}
    </div>
    </PlanContext.Provider>
  );
}

function App() {
  return (
    <Router>
      <SettingsProvider>
        <LanguageProvider>
          <ThemeToggle />
          <AppContent />
        </LanguageProvider>
      </SettingsProvider>
    </Router>
  );
}

export default App;
